/**
 * QuizCard Component
 * Displays a quiz type card with metadata
 */

import React from 'react';
import { Button } from '@voilajsx/uikit/button';
import { Clock, FileQuestion, ArrowRight } from 'lucide-react';
import type { QuizType } from '../types';

interface QuizCardProps {
  quiz: QuizType;
  onStart: (quizTypeId: number) => void;
}

export const QuizCard: React.FC<QuizCardProps> = ({ quiz, onStart }) => {
  return (
    <div className="group relative overflow-hidden bg-gradient-to-br from-card to-card/50 rounded-2xl border-2 border-border hover:border-primary transition-all hover:shadow-xl hover:scale-[1.02]">
      {/* Hover gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-teal-50/50 dark:from-emerald-950/20 dark:to-teal-950/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>

      <div className="relative z-10 p-6">
        {/* Icon & Question Count */}
        <div className="flex items-center justify-between mb-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900 dark:to-teal-900 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
            <span className="text-3xl">{quiz.icon_emoji}</span>
          </div>
          <div className="text-xs font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-primary to-emerald-600 text-white shadow-md">
            {quiz.question_count} Questions
          </div>
        </div>

        {/* Title & Description */}
        <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">{quiz.name}</h3>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2 leading-relaxed">
          {quiz.description}
        </p>

        {/* Metadata */}
        <div className="flex items-center gap-4 mb-6 pb-4 border-b border-border/50">
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full">
            <Clock className="h-3 w-3 text-primary" />
            <span className="font-medium">{quiz.estimated_time_minutes} min</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full">
            <FileQuestion className="h-3 w-3 text-primary" />
            <span className="font-medium capitalize">{quiz.slug.replace('-', ' ')}</span>
          </div>
        </div>

        {/* Start Button */}
        <Button
          onClick={() => onStart(quiz.id)}
          className="w-full bg-gradient-to-r from-primary to-emerald-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold py-3 shadow-lg hover:shadow-xl transition-all"
        >
          Start Quiz
          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </div>
  );
};
